import { createContext, useContext, useState, type ReactNode } from 'react';
import type { LimitExceededError } from '@/lib/api';

interface UpgradeModalContextValue {
  // Upgrade modal (usage-based limits)
  isOpen: boolean;
  errorData: LimitExceededError | null;
  showUpgradeModal: (errorData: LimitExceededError) => void;
  closeUpgradeModal: () => void;
  // Trial expired modal (time-based expiration)
  isTrialExpiredOpen: boolean;
  showTrialExpiredModal: () => void;
  closeTrialExpiredModal: () => void;
  // Payment failed modal (past_due subscription)
  isPaymentFailedOpen: boolean;
  showPaymentFailedModal: () => void;
  closePaymentFailedModal: () => void;
  // Subscription cancelled modal (canceled subscription, returning user)
  isSubscriptionCancelledOpen: boolean;
  showSubscriptionCancelledModal: () => void;
  closeSubscriptionCancelledModal: () => void;
  // Legacy expired modal (expired mobile subscription, upgrade to Stripe)
  isLegacyExpiredOpen: boolean;
  showLegacyExpiredModal: () => void;
  closeLegacyExpiredModal: () => void;
}

const UpgradeModalContext = createContext<UpgradeModalContextValue | undefined>(undefined);

export function UpgradeModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [errorData, setErrorData] = useState<LimitExceededError | null>(null);
  const [isTrialExpiredOpen, setIsTrialExpiredOpen] = useState(false);
  const [isPaymentFailedOpen, setIsPaymentFailedOpen] = useState(false);
  const [isSubscriptionCancelledOpen, setIsSubscriptionCancelledOpen] = useState(false);
  const [isLegacyExpiredOpen, setIsLegacyExpiredOpen] = useState(false);

  const showUpgradeModal = (data: LimitExceededError) => {
    // Check if this is a time-based block (trial expired) vs usage-based (limit exceeded)
    if (data.reason === 'trial_expired') {
      setIsTrialExpiredOpen(true);
    } else {
      setErrorData(data);
      setIsOpen(true);
    }
  };

  const closeUpgradeModal = () => {
    setIsOpen(false);
    setErrorData(null);
  };

  const showTrialExpiredModal = () => {
    setIsTrialExpiredOpen(true);
  };

  const closeTrialExpiredModal = () => {
    setIsTrialExpiredOpen(false);
  };

  const showPaymentFailedModal = () => {
    setIsPaymentFailedOpen(true);
  };

  const closePaymentFailedModal = () => {
    setIsPaymentFailedOpen(false);
  };

  const showSubscriptionCancelledModal = () => {
    setIsSubscriptionCancelledOpen(true);
  };

  const closeSubscriptionCancelledModal = () => {
    setIsSubscriptionCancelledOpen(false);
  };

  const showLegacyExpiredModal = () => {
    setIsLegacyExpiredOpen(true);
  };

  const closeLegacyExpiredModal = () => {
    setIsLegacyExpiredOpen(false);
  };

  return (
    <UpgradeModalContext.Provider value={{
      isOpen,
      errorData,
      showUpgradeModal,
      closeUpgradeModal,
      isTrialExpiredOpen,
      showTrialExpiredModal,
      closeTrialExpiredModal,
      isPaymentFailedOpen,
      showPaymentFailedModal,
      closePaymentFailedModal,
      isSubscriptionCancelledOpen,
      showSubscriptionCancelledModal,
      closeSubscriptionCancelledModal,
      isLegacyExpiredOpen,
      showLegacyExpiredModal,
      closeLegacyExpiredModal,
    }}>
      {children}
    </UpgradeModalContext.Provider>
  );
}

export function useUpgradeModal() {
  const context = useContext(UpgradeModalContext);
  if (!context) {
    throw new Error('useUpgradeModal must be used within UpgradeModalProvider');
  }
  return context;
}
